/**
 * BroadcastOfferDialog — crea la STESSA offerta dedicata per N destinatari in una volta.
 *
 * Riservato ad ADMIN e MAGAZZINO (non agenti).
 *
 * Step:
 *  1. Listino base
 *  2. Articoli + sconti (riutilizza la stessa logica del DedicatedOfferDialog)
 *  3. Destinatari: Tab "Agenti" o "Clienti diretti" con multi-select + "Seleziona tutti"
 *  4. Submit → backend crea N offerte
 *  5. Post-submit: bottoni "Condividi via WhatsApp" + "Scarica PDF" (per la prima offerta creata)
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Plus, Trash2, FileText, Save, Loader2, Sparkles, Users, Building2, Share2, Download, CheckSquare, Square, MessageCircle } from "lucide-react";
import { shareWhatsAppWithPdf } from "../utils/share";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Textarea } from "./ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const round05 = (v) => Math.round((Number(v) || 0) * 20) / 20;

export default function BroadcastOfferDialog({ open, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [pricelists, setPricelists] = useState([]);
  const [pricelistId, setPricelistId] = useState("");
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [freeOpen, setFreeOpen] = useState(false);
  const [freeForm, setFreeForm] = useState({ code: "", description: "", unit: "Kg", base_price: "" });

  const [mode, setMode] = useState("customers"); // "customers" | "agents"
  const [agents, setAgents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // {created, skipped, errors, offer_ids}

  // Reset on open
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [pl, ag, cu] = await Promise.all([
          axios.get(`${API}/pricelist-types`),
          axios.get(`${API}/agents`),
          axios.get(`${API}/customers`),
        ]);
        setPricelists(pl.data || []);
        setAgents(ag.data || []);
        setCustomers((cu.data || []).filter(c => c.is_active !== false));
      } catch (e) {
        toast.error("Errore nel caricamento dati");
      }
    })();
    // reset
    setStep(1);
    setPricelistId("");
    setProducts([]);
    setItems([]);
    setNotes("");
    setMode("customers");
    setSelectedIds(new Set());
    setSearch("");
    setResult(null);
  }, [open]);

  // Load products of pricelist
  useEffect(() => {
    if (!pricelistId) return;
    setProductsLoading(true);
    (async () => {
      try {
        // Senza customer_id: usiamo /products (prezzo via formula del listino).
        // Per semplicità riutilizziamo l'endpoint preview-catalog con un cliente fake non
        // serve, quindi usiamo direttamente l'endpoint dei prodotti e calcoliamo lato server
        // tramite parametro pricelist_type_id (l'endpoint /products supporta with_pricelist_price).
        const r = await axios.get(`${API}/products`, { params: { active_only: true, in_listino: "true" } });
        // Stima il prezzo cliente "default" usando direttamente l'oggetto listino:
        const pl = pricelists.find(p => p.id === pricelistId);
        const list = (r.data || []).map(p => {
          const acq = Number(p.acq || 0);
          const bank = Number(p.bank || 0);
          const marg = Number(p.marg || 0);
          const extra = Number(p.extra || 0);
          const base = marg > 0 && marg < 1 ? (acq * (1 + bank) / (1 - marg)) + extra : acq * (1 + bank) + extra;
          const comm = (p.commission_overrides || {})[pricelistId] ?? (pl?.commission || 0);
          const disc = (p.discount_overrides || {})[pricelistId] ?? (pl?.discount || 0);
          const markup = pl?.markup || 0;
          const fixed = (p.price_overrides || {})[pricelistId];
          let price = fixed != null && fixed > 0 ? Number(fixed) : base * (1 + comm) * (1 + markup) * (1 - disc);
          price = Math.round(price * 20) / 20;
          return {
            id: p.id, code: p.code, description: p.description,
            unit: p.unit || "Kg", vat_rate: p.vat_rate ?? 0.10,
            category_name: p.category_name || "",
            price,
          };
        });
        setProducts(list);
      } catch (e) {
        toast.error("Errore caricamento prodotti");
      } finally {
        setProductsLoading(false);
      }
    })();
  }, [pricelistId, pricelists]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    const taken = new Set(items.map(i => i.product_id));
    const base = products.filter(p => !taken.has(p.id));
    if (!q) return base.slice(0, 50);
    return base.filter(p =>
      q.split(/\s+/).every(tok =>
        (p.code || "").toLowerCase().includes(tok) ||
        (p.description || "").toLowerCase().includes(tok) ||
        (p.category_name || "").toLowerCase().includes(tok)
      )
    ).slice(0, 80);
  }, [products, productSearch, items]);

  const addProduct = (p) => {
    setItems(prev => [...prev, {
      product_id: p.id, is_free: false, code: p.code, description: p.description,
      unit: p.unit, vat_rate: p.vat_rate, base_price: p.price, discount_pct: 0,
      category_name: p.category_name,
    }]);
    setProductSearch("");
  };
  const addFreeItem = () => {
    if (!freeForm.description.trim()) { toast.error("Descrizione obbligatoria"); return; }
    const bp = Number(freeForm.base_price);
    if (!bp || bp <= 0) { toast.error("Prezzo > 0"); return; }
    setItems(prev => [...prev, {
      product_id: null, is_free: true, code: freeForm.code.trim(),
      description: freeForm.description.trim(), unit: (freeForm.unit || "Kg").trim(),
      vat_rate: 0.10, base_price: bp, discount_pct: 0, category_name: "",
    }]);
    setFreeForm({ code: "", description: "", unit: "Kg", base_price: "" });
    setFreeOpen(false);
  };
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateDiscount = (idx, val) => {
    const num = Math.max(0, Math.min(99, Number(val) || 0));
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, discount_pct: num } : it));
  };
  const computedFinal = (it) => round05(it.base_price * (1 - (it.discount_pct || 0) / 100));

  // Recipients (step 3)
  const recipientList = mode === "customers" ? customers : agents;
  const filteredRecipients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipientList;
    if (mode === "customers") {
      return recipientList.filter(c =>
        (c.business_name || "").toLowerCase().includes(q) ||
        (c.codice_cliente || "").toLowerCase().includes(q) ||
        (c.vat_number || "").toLowerCase().includes(q)
      );
    }
    return recipientList.filter(a =>
      (a.name || "").toLowerCase().includes(q) ||
      (a.access_code || "").toLowerCase().includes(q)
    );
  }, [recipientList, search, mode]);

  const toggleRecipient = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allFilteredSelected = filteredRecipients.length > 0 && filteredRecipients.every(r => selectedIds.has(r.id));
  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredRecipients.forEach(r => next.delete(r.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredRecipients.forEach(r => next.add(r.id));
        return next;
      });
    }
  };

  // Reset selection when changing mode
  useEffect(() => { setSelectedIds(new Set()); }, [mode]);

  const canStep2 = !!pricelistId;
  const canStep3 = items.length > 0;
  const canSubmit = selectedIds.size > 0;

  const submit = async () => {
    if (!pricelistId || items.length === 0 || selectedIds.size === 0) {
      toast.error("Compila tutti gli step");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        target_mode: mode,
        target_ids: Array.from(selectedIds),
        pricelist_type_id: pricelistId,
        items: items.map(it => it.is_free ? {
          product_id: null, is_free: true, code: it.code, description: it.description,
          unit: it.unit, base_price: Number(it.base_price) || 0,
          vat_rate: Number(it.vat_rate) || 0.10, discount_pct: Number(it.discount_pct) || 0,
        } : {
          product_id: it.product_id, discount_pct: Number(it.discount_pct) || 0,
        }),
        notes: notes.trim(),
      };
      const r = await axios.post(`${API}/dedicated-offers/broadcast`, payload);
      setResult(r.data);
      toast.success(`${r.data.created} offerte create`);
      if (onSaved) onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione offerte");
    } finally {
      setSaving(false);
    }
  };

  // ============ WHATSAPP SHARE ============
  const handleDownloadPdf = async (offerId) => {
    try {
      const r = await axios.get(`${API}/dedicated-offers/${offerId}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `offerta-${offerId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Errore download PDF");
    }
  };

  const handleShareWhatsapp = async (offerId) => {
    try {
      const r = await axios.get(`${API}/dedicated-offers/${offerId}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const res = await shareWhatsAppWithPdf({
        text: "Ti invio l'offerta dedicata. Buona giornata!",
        blob,
        filename: `offerta-${offerId.slice(0, 8)}.pdf`,
      });
      if (res === "shared") toast.success("Offerta condivisa con PDF allegato");
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast.error("Errore condivisione");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col" data-testid="broadcast-offer-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl text-stone-900 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#8A1C2A]" /> Broadcast offerta dedicata
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        {!result && (
          <div className="flex items-center gap-2 px-1 -mt-2 mb-2 text-[11px] font-medium">
            {["Listino", "Articoli", "Destinatari"].map((label, i) => {
              const idx = i + 1;
              const active = step === idx;
              const done = (idx === 1 && !!pricelistId) || (idx === 2 && items.length > 0) || (idx === 3 && selectedIds.size > 0);
              return (
                <React.Fragment key={label}>
                  <button
                    type="button"
                    onClick={() => {
                      if (idx === 1) setStep(1);
                      if (idx === 2 && pricelistId) setStep(2);
                      if (idx === 3 && pricelistId && items.length > 0) setStep(3);
                    }}
                    className={`px-3 py-1 rounded-full border ${active ? "bg-[#8A1C2A] text-white border-[#8A1C2A]" : done ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-100 text-stone-500 border-stone-200"}`}
                    data-testid={`bo-step-${idx}-btn`}
                  >
                    {idx}. {label}
                  </button>
                  {idx < 3 && <span className="text-stone-300">→</span>}
                </React.Fragment>
              );
            })}
          </div>
        )}

        <ScrollArea className="flex-1 pr-2">
          {/* RESULT step (post submit) */}
          {result && (
            <div className="space-y-4" data-testid="bo-result-pane">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-emerald-900">✅ Broadcast completato</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1">{result.created} offerte create</p>
                {result.skipped > 0 && <p className="text-xs text-amber-700 mt-1">{result.skipped} saltati (clienti non trovati)</p>}
                {result.errors?.length > 0 && <p className="text-xs text-red-700 mt-1">{result.errors.length} errori</p>}
              </div>

              <div>
                <p className="text-sm font-semibold text-stone-800 mb-2">Condividi PDF dell&apos;offerta</p>
                <p className="text-xs text-stone-500 mb-3">
                  La prima offerta della lista è usata come campione del PDF (tutte hanno le stesse condizioni).
                </p>
                {result.offer_ids?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleDownloadPdf(result.offer_ids[0])}
                      className="border-stone-300"
                      data-testid="bo-download-pdf"
                    >
                      <Download className="w-4 h-4 mr-1" /> Scarica PDF
                    </Button>
                    <Button
                      type="button"
                      onClick={() => handleShareWhatsapp(result.offer_ids[0])}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="bo-share-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4 mr-1" /> Condividi via WhatsApp
                    </Button>
                  </div>
                )}
                <p className="text-[10px] text-stone-400 mt-2">
                  Su iPad/telefono Android/iPhone apre il menu di condivisione (rubrica WhatsApp). Su desktop scarica il PDF e apre WhatsApp Web (allega manualmente il file).
                </p>
              </div>
            </div>
          )}

          {/* STEP 1 — Pricelist */}
          {!result && step === 1 && (
            <div className="space-y-3" data-testid="bo-step-1-pane">
              <Label className="text-sm font-medium">Listino di partenza</Label>
              <p className="text-xs text-stone-500">Il prezzo base verrà calcolato da questo listino. Poi applichi sconti % per riga.</p>
              <div className="divide-y divide-stone-100 border border-stone-200 rounded-lg overflow-hidden">
                {pricelists.map((pl) => {
                  const sel = pl.id === pricelistId;
                  return (
                    <button
                      key={pl.id}
                      type="button"
                      onClick={() => setPricelistId(pl.id)}
                      className={`w-full text-left px-4 py-3 flex items-center justify-between hover:bg-stone-50 ${sel ? "bg-[#8A1C2A]/5" : ""}`}
                      data-testid={`bo-pricelist-${pl.id}`}
                    >
                      <div>
                        <p className="font-medium text-stone-900">{pl.name}</p>
                        <p className="text-xs text-stone-500">Provv. {((pl.commission || 0) * 100).toFixed(1)}% · Ricarico {((pl.markup || 0) * 100).toFixed(1)}%</p>
                      </div>
                      {sel && <Badge className="bg-[#8A1C2A] text-white">Selezionato</Badge>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 2 — Items */}
          {!result && step === 2 && (
            <div className="space-y-3" data-testid="bo-step-2-pane">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Aggiungi articoli</Label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input className="pl-10" placeholder="Cerca codice, descrizione, categoria..." value={productSearch} onChange={(e) => setProductSearch(e.target.value)} data-testid="bo-product-search" />
                  </div>
                  <div className="border border-stone-200 rounded-lg max-h-[50vh] overflow-y-auto divide-y divide-stone-100">
                    {productsLoading && <div className="px-4 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
                    {!productsLoading && filteredProducts.map((p) => (
                      <button key={p.id} type="button" onClick={() => addProduct(p)} className="w-full text-left px-3 py-2 flex items-center justify-between gap-2 hover:bg-stone-50" data-testid={`bo-add-product-${p.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-stone-900 truncate">{p.description}</p>
                          <p className="text-[11px] text-stone-500 font-mono">{p.code} · {p.category_name || "—"}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-[#8A1C2A]">€ {Number(p.price).toFixed(2)}</p>
                          <Plus className="w-4 h-4 inline text-emerald-600" />
                        </div>
                      </button>
                    ))}
                    {!productsLoading && filteredProducts.length === 0 && (
                      <div className="px-4 py-8 text-center text-sm text-stone-500">{productSearch ? "Nessun prodotto" : "Tutti gli articoli sono già nell'offerta"}</div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Articoli in offerta ({items.length})</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setFreeOpen(true)} className="text-amber-700 border-amber-300 hover:bg-amber-50 h-7 text-xs" data-testid="bo-add-free-btn">
                      <Plus className="w-3 h-3 mr-1" /> Articolo libero
                    </Button>
                  </div>
                  {freeOpen && (
                    <div className="border-2 border-amber-300 bg-amber-50/40 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-semibold text-amber-800 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Articolo libero</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={freeForm.code} onChange={(e) => setFreeForm(f => ({ ...f, code: e.target.value }))} className="h-8 text-sm" placeholder="Codice (opz.)" />
                        <Input value={freeForm.unit} onChange={(e) => setFreeForm(f => ({ ...f, unit: e.target.value }))} className="h-8 text-sm" placeholder="U.M." />
                      </div>
                      <Input value={freeForm.description} onChange={(e) => setFreeForm(f => ({ ...f, description: e.target.value }))} className="h-8 text-sm" placeholder="Descrizione *" />
                      <Input type="number" min="0" step="0.01" value={freeForm.base_price} onChange={(e) => setFreeForm(f => ({ ...f, base_price: e.target.value }))} className="h-8 text-sm" placeholder="Prezzo base €" />
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => setFreeOpen(false)}>Annulla</Button>
                        <Button size="sm" onClick={addFreeItem} className="bg-amber-600 text-white">Aggiungi</Button>
                      </div>
                    </div>
                  )}
                  <div className="border border-stone-200 rounded-lg max-h-[50vh] overflow-y-auto divide-y divide-stone-100">
                    {items.length === 0 && <div className="px-4 py-12 text-center text-sm text-stone-500">Nessun articolo</div>}
                    {items.map((it, idx) => (
                      <div key={it.product_id || `free-${idx}`} className={`px-3 py-2 space-y-1 ${it.is_free ? "bg-amber-50/30" : ""}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-stone-900 truncate">{it.description}</p>
                            <p className="text-[11px] text-stone-500 font-mono">{it.code || "—"}</p>
                          </div>
                          <button onClick={() => removeItem(idx)} className="text-rose-600 hover:bg-rose-50 rounded p-1"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 items-end">
                          <div>
                            <Label className="text-[10px] text-stone-500 uppercase">Listino</Label>
                            <div className="h-8 text-sm text-stone-700 px-2 py-1 bg-stone-50 rounded border border-stone-200">€ {Number(it.base_price).toFixed(2)}</div>
                          </div>
                          <div>
                            <Label className="text-[10px] text-stone-500 uppercase">Sconto %</Label>
                            <Input type="number" min="0" max="99" step="0.5" value={it.discount_pct} onChange={(e) => updateDiscount(idx, e.target.value)} className="h-8 text-sm" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-stone-500 uppercase">Dedicato</Label>
                            <div className="h-8 text-sm font-bold text-[#8A1C2A] px-2 py-1 bg-amber-50 rounded border border-amber-200 text-right">€ {computedFinal(it).toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <Separator />
              <div>
                <Label className="text-sm font-medium">Note (opzionali, sul PDF)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="text-sm" placeholder="Es. Validità, condizioni..." />
              </div>
            </div>
          )}

          {/* STEP 3 — Destinatari */}
          {!result && step === 3 && (
            <div className="space-y-3" data-testid="bo-step-3-pane">
              {/* Mode switch */}
              <div className="flex gap-2 bg-stone-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setMode("agents")}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium flex items-center justify-center gap-2 ${mode === "agents" ? "bg-white text-[#8A1C2A] shadow-sm" : "text-stone-600"}`}
                  data-testid="bo-mode-agents"
                >
                  <Users className="w-4 h-4" /> Agenti ({agents.length})
                </button>
                <button
                  type="button"
                  onClick={() => setMode("customers")}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium flex items-center justify-center gap-2 ${mode === "customers" ? "bg-white text-[#8A1C2A] shadow-sm" : "text-stone-600"}`}
                  data-testid="bo-mode-customers"
                >
                  <Building2 className="w-4 h-4" /> Clienti diretti ({customers.length})
                </button>
              </div>

              <p className="text-xs text-stone-500">
                {mode === "agents"
                  ? "Invia l'offerta agli AGENTI selezionati. Ogni offerta verrà creata per TUTTI i clienti dell'agente — l'agente potrà poi rimuovere quelle che non vuole inoltrare."
                  : "Invia l'offerta DIRETTAMENTE ai clienti selezionati."}
              </p>

              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input className="pl-10 h-10" placeholder={mode === "agents" ? "Cerca agente..." : "Cerca cliente per nome, codice, P.IVA..."} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="bo-recipient-search" />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-stone-500">{filteredRecipients.length} {mode === "agents" ? "agenti" : "clienti"} · {selectedIds.size} selezionat{selectedIds.size === 1 ? "o" : "i"}</span>
                <Button type="button" variant="outline" size="sm" onClick={toggleSelectAll} className="border-[#8A1C2A] text-[#8A1C2A] hover:bg-[#8A1C2A]/5" data-testid="bo-select-all">
                  {allFilteredSelected ? <><Square className="w-3 h-3 mr-1" /> Deseleziona tutti</> : <><CheckSquare className="w-3 h-3 mr-1" /> Seleziona tutti</>}
                </Button>
              </div>

              <div className="border border-stone-200 rounded-lg max-h-[45vh] overflow-y-auto divide-y divide-stone-100">
                {filteredRecipients.map(r => {
                  const sel = selectedIds.has(r.id);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => toggleRecipient(r.id)}
                      className={`w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-stone-50 ${sel ? "bg-[#8A1C2A]/5" : ""}`}
                      data-testid={`bo-recipient-${r.id}`}
                    >
                      <input type="checkbox" checked={sel} readOnly className="w-4 h-4 accent-[#8A1C2A] cursor-pointer" />
                      <div className="flex-1 min-w-0">
                        {mode === "agents" ? (
                          <>
                            <p className="font-medium text-stone-900 truncate">{r.name}</p>
                            <p className="text-xs text-stone-500">Codice: {r.access_code} · {((r.commission_rate || 0) * 100).toFixed(1)}%</p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-stone-900 truncate">{r.business_name}</p>
                            <p className="text-xs text-stone-500">{r.codice_cliente} · {r.vat_number || "—"}</p>
                          </>
                        )}
                      </div>
                    </button>
                  );
                })}
                {filteredRecipients.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-stone-500">Nessun risultato</div>
                )}
              </div>
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex-row items-center justify-between gap-2 pt-3 border-t border-stone-100">
          {!result ? (
            <>
              <div className="text-xs text-stone-500">
                {pricelistId && <>Listino: <strong>{pricelists.find(p => p.id === pricelistId)?.name}</strong></>}
                {items.length > 0 && <> · {items.length} articol{items.length === 1 ? "o" : "i"}</>}
                {selectedIds.size > 0 && <> · {selectedIds.size} destinatari</>}
              </div>
              <div className="flex gap-2">
                {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="bo-back">Indietro</Button>}
                {step < 3 && (
                  <Button onClick={() => setStep(step + 1)} disabled={(step === 1 && !canStep2) || (step === 2 && !canStep3)} className="bg-[#8A1C2A]" data-testid="bo-next">
                    Avanti →
                  </Button>
                )}
                {step === 3 && (
                  <Button onClick={submit} disabled={!canSubmit || saving} className="bg-[#8A1C2A] hover:bg-[#6e1622] text-white" data-testid="bo-submit">
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Invia broadcast ({selectedIds.size})
                  </Button>
                )}
              </div>
            </>
          ) : (
            <Button onClick={onClose} className="bg-[#8A1C2A] hover:bg-[#6e1622] text-white" data-testid="bo-close">Chiudi</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
